import { BorrowHistoryItem, MatsForBorrow } from "@mezo/api"
import { MusdDebtInfo, MusdLiquityProps } from "@mezo/sdk"
import { createSlice } from "@reduxjs/toolkit"

import {
  BorrowCollateralManagementStrategy,
  BorrowMoreStrategy,
  BorrowPartialRepayStrategy,
  BorrowRepayStrategy,
} from "#/shared/types/borrow"

type MusdState = {
  isLoadingBorrowData: boolean
  collateralPrice: bigint | null
  liquity: MusdLiquityProps | null
  currentLoan: MusdDebtInfo | null
  matsForBorrow: MatsForBorrow | null
  borrowHistory: BorrowHistoryItem[]
  isLoanFlow: boolean
  loanAmount: string
  collateralAmount: string
  updateLoanAmount: string
  repayLoanAmount: string
  updateStrategy: BorrowMoreStrategy | null
  repayStrategy: BorrowRepayStrategy | null
  partialRepaymentStrategy: BorrowPartialRepayStrategy | null
  updateCollateralStrategy: BorrowCollateralManagementStrategy | null
  updateCollateralAmount: string
  updateCollateralRatio: string
}

const initialState: MusdState = {
  isLoadingBorrowData: false,
  collateralPrice: null,
  liquity: null,
  currentLoan: null,
  matsForBorrow: null,
  borrowHistory: [],
  // Borrow modals state values
  isLoanFlow: false,
  // new loan state
  loanAmount: "",
  collateralAmount: "",
  // get more musd state
  updateLoanAmount: "",
  // repay loan state
  repayLoanAmount: "",
  updateStrategy: null,
  repayStrategy: null,
  partialRepaymentStrategy: null,
  // manage collateral state
  updateCollateralStrategy: null,
  updateCollateralAmount: "",
  updateCollateralRatio: "",
}

export const musdSlice = createSlice({
  name: "borrow",
  initialState,
  reducers: {
    setIsLoadingBorrowData: (immerState, { payload }: { payload: boolean }) => {
      immerState.isLoadingBorrowData = payload
    },
    setIsLoanFlow: (immerState, { payload }: { payload: boolean }) => {
      immerState.isLoanFlow = payload
    },
    setLoanAmount: (immerState, { payload }: { payload: string }) => {
      immerState.loanAmount = payload
    },
    setCollateralAmount: (immerState, { payload }: { payload: string }) => {
      immerState.collateralAmount = payload
    },
    setUpdateLoanAmount: (immerState, { payload }: { payload: string }) => {
      immerState.updateLoanAmount = payload
    },
    setRepayLoanAmount: (immerState, { payload }: { payload: string }) => {
      immerState.repayLoanAmount = payload
    },
    setCollateralPrice: (immerState, { payload }: { payload: bigint }) => {
      immerState.collateralPrice = payload
    },
    setUpdateStrategy: (
      immerState,
      { payload }: { payload: BorrowMoreStrategy },
    ) => {
      immerState.updateStrategy = payload
    },
    setRepayStrategy: (
      immerState,
      { payload }: { payload: BorrowRepayStrategy },
    ) => {
      immerState.repayStrategy = payload
    },
    setPartialRepayStrategy: (
      immerState,
      { payload }: { payload: BorrowPartialRepayStrategy },
    ) => {
      immerState.partialRepaymentStrategy = payload
    },
    setUpdateCollateralStrategy: (
      immerState,
      { payload }: { payload: BorrowCollateralManagementStrategy },
    ) => {
      immerState.updateCollateralStrategy = payload
    },
    setUpdateCollateralAmount: (
      immerState,
      { payload }: { payload: string },
    ) => {
      immerState.updateCollateralAmount = payload
    },
    setUpdateCollateralRatio: (
      immerState,
      { payload }: { payload: string },
    ) => {
      immerState.updateCollateralRatio = payload
    },
    setBorrowLiquity: (
      immerState,
      { payload }: { payload: MusdLiquityProps },
    ) => {
      immerState.liquity = payload
    },
    setUserLoan: (immerState, { payload }: { payload: MusdDebtInfo }) => {
      immerState.currentLoan = payload
    },
    setMatsForBorrow: (
      immerState,
      { payload }: { payload: MatsForBorrow | null },
    ) => {
      immerState.matsForBorrow = payload
    },
    setBorrowHistory: (
      immerState,
      { payload }: { payload: BorrowHistoryItem[] },
    ) => {
      immerState.borrowHistory = payload
    },
    resetBorrowFlow: (immerState) => ({
      ...immerState,
      isLoanFlow: false,
      collateralAmount: "",
      loanAmount: "",
      updateLoanAmount: "",
      repayLoanAmount: "",
      updateStrategy: null,
      repayStrategy: null,
      partialRepaymentStrategy: null,
    }),
    resetBorrowState: (_) => initialState,
  },
})

export const {
  setIsLoadingBorrowData,
  setIsLoanFlow,
  setLoanAmount,
  setUpdateLoanAmount,
  setUpdateStrategy,
  setPartialRepayStrategy,
  setRepayStrategy,
  setRepayLoanAmount,
  setUpdateCollateralStrategy,
  setUpdateCollateralRatio,
  setUpdateCollateralAmount,
  setCollateralAmount,
  setCollateralPrice,
  setBorrowLiquity,
  setUserLoan,
  setMatsForBorrow,
  setBorrowHistory,
  resetBorrowFlow,
  resetBorrowState,
} = musdSlice.actions

export default musdSlice.reducer
